import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  LogOut,
  User,
  Home,
  Menu,
  X,
  MapPin,
  Tag,
  Package,
  Settings,
  Network,
  Shield,
  Lock,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import skyAngelLogo from "../../img/skyangel.png";

export const NavbarBlack: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const { usuario, rol, logout, tienePermiso } = useAuth();

  const handleLogout = () => {
    logout();
  };

  // Cerrar dropdowns al hacer click fuera
  useEffect(() => {
    const handleClickOutside = () => {
      setIsProfileOpen(false);
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const styles = `
    .black-navbar {
      font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      background: rgba(0, 0, 0, 0.98);
      backdrop-filter: blur(20px);
      border-bottom: 1px solid rgba(0, 122, 255, 0.2);
      box-shadow: 0 2px 20px rgba(0, 0, 0, 0.8);
      position: sticky;
      top: 0;
      z-index: 1000;
    }

    .navbar-container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;
    }

    .navbar-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 72px;
    }

    .logo-container {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .logo-image {
      width: 32px;
      height: 32px;
      object-fit: contain;
      filter: brightness(1.1) drop-shadow(0 2px 4px rgba(0, 122, 255, 0.3));
      transition: all 0.3s ease;
    }

    .logo-image:hover {
      filter: brightness(1.3) drop-shadow(0 4px 8px rgba(0, 122, 255, 0.5));
      transform: scale(1.05);
    }

    .black-logo {
      color: #ffffff;
      text-decoration: none;
      font-size: 16px;
      font-weight: 700;
      letter-spacing: 2px;
      position: relative;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      gap: 10px;
      flex-shrink: 0;
    }

    .black-logo:hover {
      color: #007AFF;
      text-shadow: 0 0 10px rgba(0, 122, 255, 0.5);
    }

    .logo-text {
      position: relative;
    }

    .logo-text::after {
      content: '';
      position: absolute;
      bottom: -4px;
      left: 0;
      width: 100%;
      height: 2px;
      background: linear-gradient(90deg, transparent, #007AFF, transparent);
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    .black-logo:hover .logo-text::after {
      opacity: 1;
    }

    .desktop-menu {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .nav-link {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 10px 12px;
      color: rgba(255, 255, 255, 0.85);
      text-decoration: none;
      border-radius: 10px;
      font-size: 13px;
      font-weight: 500;
      transition: all 0.3s ease;
      position: relative;
      white-space: nowrap;
      overflow: hidden;
    }

    .nav-link::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(0, 122, 255, 0.1), transparent);
      transition: left 0.5s ease;
    }

    .nav-link:hover {
      color: #ffffff;
      background: rgba(0, 122, 255, 0.1);
      border: 1px solid rgba(0, 122, 255, 0.2);
      transform: translateY(-1px);
    }

    .nav-link:hover::before {
      left: 100%;
    }

    .nav-button {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 16px;
      color: rgba(255, 255, 255, 0.85);
      background: transparent;
      border: none;
      border-radius: 10px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;
      position: relative;
      overflow: hidden;
    }

    .nav-button::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(0, 122, 255, 0.1), transparent);
      transition: left 0.5s ease;
    }

    .nav-button:hover {
      color: #ffffff;
      background: rgba(0, 122, 255, 0.1);
      border: 1px solid rgba(0, 122, 255, 0.2);
      transform: translateY(-1px);
    }

    .nav-button:hover::before {
      left: 100%;
    }

    .relative {
      position: relative;
    }

    .dropdown-menu {
      position: absolute;
      top: 100%;
      right: 0;
      background: rgba(0, 0, 0, 0.95);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(0, 122, 255, 0.2);
      border-radius: 12px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.8);
      min-width: 220px;
      padding: 8px;
      margin-top: 8px;
      animation: dropdownSlide 0.2s ease-out;
      z-index: 1001;
    }

    @keyframes dropdownSlide {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .dropdown-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      color: rgba(255, 255, 255, 0.85);
      text-decoration: none;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 400;
      transition: all 0.2s ease;
    }

    .dropdown-item:hover {
      color: #ffffff;
      background: rgba(0, 122, 255, 0.1);
      transform: translateX(2px);
    }

    .dropdown-button {
      display: flex;
      align-items: center;
      gap: 12px;
      width: 100%;
      padding: 12px 16px;
      color: rgba(255, 255, 255, 0.85);
      background: transparent;
      border: none;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 400;
      text-align: left;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .dropdown-button:hover {
      color: #ffffff;
      background: rgba(0, 122, 255, 0.1);
      transform: translateX(2px);
    }

    .user-info {
      padding: 16px;
      border-bottom: 1px solid rgba(0, 122, 255, 0.2);
      margin-bottom: 8px;
    }

    .user-name {
      color: #ffffff;
      font-weight: 500;
      font-size: 15px;
      margin-bottom: 4px;
    }

    .user-email {
      color: rgba(255, 255, 255, 0.6);
      font-size: 13px;
    }

    .mobile-menu-button {
      display: none;
      color: rgba(255, 255, 255, 0.9);
      background: transparent;
      border: 1px solid rgba(0, 122, 255, 0.2);
      padding: 10px;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .mobile-menu-button:hover {
      color: #ffffff;
      background: rgba(0, 122, 255, 0.1);
      border-color: rgba(0, 122, 255, 0.4);
    }

    .mobile-menu {
      position: fixed;
      top: 72px;
      left: 0;
      right: 0;
      background: rgba(0, 0, 0, 0.98);
      backdrop-filter: blur(20px);
      border-bottom: 1px solid rgba(0, 122, 255, 0.2);
      padding: 20px;
      animation: mobileSlide 0.3s ease-out;
      max-height: calc(100vh - 72px);
      overflow-y: auto;
    }

    @keyframes mobileSlide {
      from {
        opacity: 0;
        transform: translateY(-20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .mobile-nav-link {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 16px 20px;
      color: rgba(255, 255, 255, 0.85);
      text-decoration: none;
      border-radius: 12px;
      font-size: 16px;
      font-weight: 500;
      margin-bottom: 8px;
      transition: all 0.3s ease;
    }

    .mobile-nav-link:hover {
      color: #ffffff;
      background: rgba(0, 122, 255, 0.1);
      transform: translateX(4px);
    }

    .mobile-user-section {
      margin-top: 24px;
      padding-top: 24px;
      border-top: 1px solid rgba(0, 122, 255, 0.2);
    }

    .mobile-user-info {
      padding: 16px 20px;
      margin-bottom: 16px;
    }

    .mobile-logout-button {
      display: flex;
      align-items: center;
      gap: 16px;
      width: 100%;
      padding: 16px 20px;
      color: #ff6b6b;
      background: transparent;
      border: 1px solid rgba(255, 107, 107, 0.2);
      border-radius: 12px;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .mobile-logout-button:hover {
      background: rgba(255, 107, 107, 0.1);
      border-color: rgba(255, 107, 107, 0.4);
    }

    /* Responsive */
    @media (max-width: 768px) {
      .desktop-menu {
        display: none;
      }

      .mobile-menu-button {
        display: block;
      }

      .black-logo {
        font-size: 16px;
        letter-spacing: 2px;
      }

      .logo-image {
        width: 32px;
        height: 32px;
      }
    }

    /* Scrollbar para mobile menu */
    .mobile-menu::-webkit-scrollbar {
      width: 6px;
    }

    .mobile-menu::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.1);
    }

    .mobile-menu::-webkit-scrollbar-thumb {
      background: rgba(0, 122, 255, 0.3);
      border-radius: 3px;
    }

    .mobile-menu::-webkit-scrollbar-thumb:hover {
      background: rgba(0, 122, 255, 0.5);
    }
  `;

  return (
    <>
      <style>{styles}</style>
      <nav className="black-navbar">
        <div className="navbar-container">
          <div className="navbar-content">
            {/* Logo */}
            <div className="logo-container">
              <Link to="/welcome-black" className="black-logo">
                <img 
                  src={skyAngelLogo} 
                  alt="SkyAngel Logo" 
                  className="logo-image"
                />
                <span className="logo-text">{usuario?.plataforma?.nombre || 'Plataforma'}</span>
              </Link>
            </div>

            {/* Desktop Menu */}
            <div className="desktop-menu">
              <Link to="/welcome-black" className="nav-link">
                <Home size={18} />
                Inicio
              </Link>

              {/* Tags */}
              {tienePermiso('tags.ver') && (
                <Link to="/tags" className="nav-link">
                  <Tag size={18} />
                  Tags
                </Link>
              )}

              {/* Productos Dinámicos */}
              {tienePermiso('productos.ver') && (
                <Link to="/productos-dinamicos" className="nav-link">
                  <Package size={18} />
                  Productos
                </Link>
              )}

              {/* Variables Productos */}
              {tienePermiso('variables.ver') && (
                <Link to="/variables-productos" className="nav-link">
                  <Settings size={18} />
                  Tipos
                </Link>
              )}

              {/* Usuarios */}
              {tienePermiso('usuarios.ver') && (
                <Link to="/usuarios" className="nav-link">
                  <User size={18} />
                  Usuarios
                </Link>
              )}

              {/* Subcuentas */}
              {tienePermiso('subcuentas.ver') && (
                <Link to="/subcuentas" className="nav-link">
                  <Network size={18} />
                  Subcuentas
                </Link>
              )}

              {/* Roles */}
              {tienePermiso('roles.ver') && (
                <Link to="/roles" className="nav-link">
                  <Shield size={18} />
                  Roles
                </Link>
              )}

              {/* Permisos */}
              {tienePermiso('permisos.ver') && (
                <Link to="/permisos" className="nav-link">
                  <Lock size={18} />
                  Permisos
                </Link>
              )}

              {/* Mapa */}
              {tienePermiso('mapa.ver') && (
                <Link to="/mapa" className="nav-link">
                  <MapPin size={18} />
                  Mapa
                </Link>
              )}

              {/* Profile Dropdown */}
              <div className="relative" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="nav-button"
                >
                  <User size={18} />
                  {usuario?.nombre}
                  <ChevronDown size={16} />
                </button>

                {isProfileOpen && (
                  <div className="dropdown-menu">
                    <div className="user-info">
                      <div className="user-name">
                        {usuario?.nombre} {usuario?.apellido_paterno}
                      </div>
                      <div className="user-email">{usuario?.correo}</div>
                    </div>
                    <button onClick={handleLogout} className="dropdown-button">
                      <LogOut size={16} />
                      Cerrar Sesión
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="mobile-menu-button"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="mobile-menu">
              {/* Logo en mobile menu */}
              <div className="flex items-center justify-center mb-6 pb-4 border-b border-white/10">
                <img 
                  src={skyAngelLogo} 
                  alt="SkyAngel Logo" 
                  className="w-8 h-8 object-contain mr-3"
                />
                <span className="text-white font-bold text-lg tracking-wider">{usuario?.plataforma?.nombre || 'Plataforma'}</span>
              </div>
              
              <Link
                to="/welcome-black"
                className="mobile-nav-link"
                onClick={() => setIsMenuOpen(false)}
              >
                <Home size={20} />
                Inicio
              </Link>
              {tienePermiso('tags.ver') && (
                <Link to="/tags" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
                  <Tag size={20} /> Tags
                </Link>
              )}
              {tienePermiso('productos.ver') && (
                <Link to="/productos-dinamicos" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
                  <Package size={20} /> Productos
                </Link>
              )}
              {tienePermiso('variables.ver') && (
                <Link to="/variables-productos" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
                  <Settings size={20} /> Tipos
                </Link>
              )}
              {tienePermiso('usuarios.ver') && (
                <Link to="/usuarios" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
                  <User size={20} /> Usuarios
                </Link>
              )}
              {tienePermiso('subcuentas.ver') && (
                <Link to="/subcuentas" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
                  <Network size={20} /> Subcuentas
                </Link>
              )}

              {/* Roles */}
              {tienePermiso('roles.ver') && (
                <Link to="/roles" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
                  <Shield size={20} />
                  Roles
                </Link>
              )}

              {/* Permisos */}
              {tienePermiso('permisos.ver') && (
                <Link to="/permisos" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
                  <Lock size={20} />
                  Permisos
                </Link>
              )}
              {tienePermiso('mapa.ver') && (
                <Link to="/mapa" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
                  <MapPin size={20} /> Mapa
                </Link>
              )}

              <div className="mobile-user-section">
                <div className="mobile-user-info">
                  <div className="user-name">
                    {usuario?.nombre} {usuario?.apellido_paterno}
                  </div>
                  <div className="user-email">{usuario?.correo}</div>
                </div>
                <button onClick={handleLogout} className="mobile-logout-button">
                  <LogOut size={20} />
                  Cerrar Sesión
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};
